import React from 'react';
import { AlertTriangleIcon, ChevronDownIcon, ChevronRightIcon, RotateCcwIcon, SearchIcon, XIcon } from 'lucide-react';
import type { LoanResult, StaffMember } from '../types/commission';
import { FULL_COMP_BPS, tierFor } from '../utils/commission';
import { bps, percent, shortDate, usd } from '../utils/format';
import { PointsBadge } from './PointsBadge';
export interface PersonRow {
  staff: StaffMember;
  total: number;
  files: {
    result: LoanResult;
    entitledBps: number;
    baselineBps: number;
    isOverridden: boolean;
    shareOfFullComp: number;
    payoutBps: number;
    payoutDollars: number;
  }[];
}
interface PersonPayoutsProps {
  rows: PersonRow[];
  expanded: Record<string, boolean>;
  query: string;
  onQueryChange: (value: string) => void;
  onToggle: (staffId: string) => void;
  onOverride: (loanId: string, staffId: string, value: number | undefined) => void;
}
export function PersonPayouts({
  rows,
  expanded,
  query,
  onQueryChange,
  onToggle,
  onOverride
}: PersonPayoutsProps) {
  return <section aria-labelledby="payouts-heading" className="space-y-3" data-id="element-19" data-mp="6o9zb5yqlelw">
      <div className="flex flex-wrap items-center justify-between gap-3" data-id="element-20" data-mp="bgt8lqqe5nkw">
        <div data-id="element-21" data-mp="3tusrspecs97v">
          <h2 id="payouts-heading" className="text-base font-semibold text-navy" data-id="element-22" data-mp="3tv2h0arkqcci">
            Commission by person
          </h2>
          <p className="text-xs text-ink-3" data-id="element-23" data-mp="1rjg49rmy2w9">
            Highest earner first · expand a person to see file detail and adjust their
            rate
          </p>
        </div>

        <div className="relative w-full sm:w-72" data-id="element-24" data-mp="ufnrnbww1a6v">
          <label htmlFor="person-search" className="sr-only" data-id="element-25" data-mp="udb7qt7kkpr6">
            Search people by name
          </label>
          <SearchIcon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-ink-3" data-id="element-26" data-mp="lepikf7lechf" />
          <input id="person-search" type="search" value={query} onChange={event => onQueryChange(event.target.value)} placeholder="Search by name…" className="w-full rounded-sm border border-line-strong bg-surface py-2 pl-9 pr-9 text-sm text-ink placeholder:text-ink-3 transition-colors duration-150 ease-out focus:border-orange focus:outline-none focus-visible:ring-2 focus-visible:ring-orange" data-id="element-27" data-mp="315c4251ohghz" />
          {query.length > 0 && <button type="button" onClick={() => onQueryChange('')} aria-label="Clear search" className="absolute right-2 top-1/2 -translate-y-1/2 rounded-sm p-1 text-ink-3 transition-colors duration-150 ease-out hover:bg-raised hover:text-ink focus:outline-none focus-visible:ring-2 focus-visible:ring-orange" data-id="element-28" data-mp="xy4mpxho0dss">
              <XIcon className="h-3.5 w-3.5" data-id="element-29" data-mp="eyz4h44bo2bw" />
            </button>}
        </div>
      </div>

      <ul className="divide-y divide-line overflow-hidden rounded-sm border border-line bg-surface shadow-panel" data-id="element-30" data-mp="2delmtt6ww1v5">
        <li className="flex items-center gap-4 border-b border-line bg-navy-soft px-4 py-2.5 text-[11px] font-semibold uppercase tracking-[0.08em] text-navy" data-id="element-31" data-mp="29djl95pivdq0">
          <span className="w-6" data-id="element-32" data-mp="3u094agm4d6" />
          <span className="flex-1" data-id="element-33" data-mp="30sbzzrl8tg">Person</span>
          <span className="w-24" data-id="element-34" data-mp="1pknp377gx2">Rate</span>
          <span className="w-16 text-right" data-id="element-35" data-mp="4uac4jymthxf">Files</span>
          <span className="w-32 text-right" data-id="element-36" data-mp="4wn1vjamswl1">Loan volume</span>
          <span className="w-32 text-right" data-id="element-37" data-mp="8uua1pr14pkm">Commission</span>
        </li>

        {rows.length === 0 && <li className="px-4 py-10 text-center" data-id="element-38" data-mp="38ctw6z75phh5">
            <p className="text-sm font-medium text-ink" data-id="element-39" data-mp="3da2fj341jrjz">No one matches “{query}”</p>
            <p className="mt-1 text-xs text-ink-3" data-id="element-40" data-mp="3dc6qu649rsua">
              Try a different name, or clear the search to see the full run.
            </p>
          </li>}

        {rows.map(row => {
        const isOpen = Boolean(expanded[row.staff.id]);
        const isLO = row.staff.role === 'LO';
        const tier = tierFor(row.staff.priorMonthLoans);
        const volume = row.files.reduce((sum, f) => sum + f.result.loan.loanAmount, 0);
        const hasOverride = row.files.some(f => f.isOverridden);
        return <li key={row.staff.id} data-id="element-41" data-mp="v6dqqfeyludi">
              <button type="button" onClick={() => onToggle(row.staff.id)} aria-expanded={isOpen} className={`flex w-full items-center gap-4 px-4 py-3 text-left transition-colors duration-150 ease-out hover:bg-raised focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-orange ${isOpen ? 'bg-orange-soft' : ''}`} data-id="element-42" data-mp="1942f3x88jjnb">
                <span className="w-6 text-ink-3" data-id="element-43" data-mp="1sri41ga13i9f">
                  {isOpen ? <ChevronDownIcon className="h-4 w-4" data-id="element-44" data-mp="1ntqfmw8lee9z" /> : <ChevronRightIcon className="h-4 w-4" data-id="element-45" data-mp="21u15bvy61mtk" />}
                </span>

                <span className="flex min-w-0 flex-1 items-center gap-3" data-id="element-46" data-mp="1wuy3slhe9yqc">
                  <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-navy text-[11px] font-bold text-white" data-id="element-47" data-mp="2gf6pc9el5sz8">
                    {initials(row.staff.name)}
                  </span>
                  <span className="min-w-0" data-id="element-48" data-mp="27fs1lhpszwif">
                    <span className="block truncate text-sm font-semibold text-ink" data-id="element-49" data-mp="27h30p48b0g37">
                      {row.staff.name}
                    </span>
                    <span className="block text-xs text-ink-3" data-id="element-50" data-mp="1xox8pui1xyo0">
                      {isLO ? `Loan officer · Tier ${tier.tier} (${row.staff.priorMonthLoans} prior closings)` : `Loan officer assistant${row.staff.hasHourlyComponent ? ' · plus hourly' : ''}`}
                    </span>
                  </span>
                </span>

                <span className="flex w-24 items-center gap-1.5" data-id="element-51" data-mp="1er4inssj1j81">
                  <span className="font-mono text-sm tabular-nums text-ink-2" data-id="element-52" data-mp="1joe4yzkmdern">
                    {bps(isLO ? tier.bps : row.staff.flatBps ?? 0, 0)}
                  </span>
                  {hasOverride && <span className="rounded-sm bg-orange px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wide text-white" data-id="element-53" data-mp="3gn3e5hl5tmtx">
                      Adj
                    </span>}
                </span>

                <span className="w-16 text-right font-mono text-sm tabular-nums text-ink-2" data-id="element-54" data-mp="37l3usimoa6nt">
                  {row.files.length}
                </span>
                <span className="w-32 text-right font-mono text-sm tabular-nums text-ink-2" data-id="element-55" data-mp="38qk9qqaw7w4">
                  {usd(volume)}
                </span>
                <span className="w-32 text-right font-mono text-base font-semibold tabular-nums text-ink" data-id="element-56" data-mp="3vevr5mxheosh">
                  {usd(row.total)}
                </span>
              </button>

              {isOpen && <div className="border-t border-line bg-raised px-4 py-3" data-id="element-57" data-mp="2eyj4llhxo0lu">
                  <div className="overflow-x-auto" data-id="element-58" data-mp="2f0m3kv12d42p">
                    <table className="w-full min-w-[1100px] border-collapse text-sm" data-id="element-59" data-mp="uh9xo0p6x33g">
                      <caption className="sr-only" data-id="element-60" data-mp="zf1yjk7tbzjs">
                        Files credited to {row.staff.name}
                      </caption>
                      <thead data-id="element-61" data-mp="13flyswnonja5">
                        <tr className="border-b border-line text-left text-[11px] font-semibold uppercase tracking-[0.06em] text-ink-3" data-id="element-62" data-mp="13fc794mzp42c">
                          <th scope="col" className="py-2 pr-3 font-semibold" data-id="element-63" data-mp="1d7rpm3277unr">
                            File
                          </th>
                          <th scope="col" className="px-2 py-2 text-right font-semibold" data-id="element-64" data-mp="1i85x9jzw12l9">
                            Loan amount
                          </th>
                          <th scope="col" className="px-2 py-2 text-right font-semibold" data-id="element-65" data-mp="1r5q0uzl83az1">
                            YSP
                          </th>
                          <th scope="col" className="px-2 py-2 text-right font-semibold" data-id="element-66" data-mp="3oyvieay81jax">
                            Processing fee
                          </th>
                          <th scope="col" className="px-2 py-2 text-right font-semibold" data-id="element-67" data-mp="3a0axj3v1e48o">
                            Discount points
                          </th>
                          <th scope="col" className="px-2 py-2 text-right font-semibold" data-id="element-68" data-mp="3iupedav7tmtw">
                            Underwriting fee
                          </th>
                          <th scope="col" className="px-2 py-2 text-right font-semibold" data-id="element-69" data-mp="3euyfskzg62et">
                            Net comp
                          </th>
                          <th scope="col" className="px-2 py-2 font-semibold" data-id="element-70" data-mp="2m587iskhxuqm">
                            Comp on file
                          </th>
                          <th scope="col" className="px-2 py-2 font-semibold" data-id="element-71" data-mp="2v4l9l06ajh1b">
                            Their rate
                          </th>
                          <th scope="col" className="py-2 pl-2 text-right font-semibold" data-id="element-72" data-mp="fc5qslnhf55f">
                            Payout
                          </th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-line" data-id="element-73" data-mp="iu3904xguak6">
                        {row.files.map(file => {
                    const {
                      loan,
                      math
                    } = file.result;
                    return <tr key={loan.id} className="align-top" data-id="element-74" data-mp="8zbexb33e5u5">
                              <td className="py-3 pr-3" data-id="element-75" data-mp="dx3622vwxnxa">
                                <span className="block text-sm font-medium text-ink" data-id="element-76" data-mp="11m4ie17v9j7u">
                                  {loan.borrower}
                                </span>
                                <span className="block font-mono text-xs text-ink-3" data-id="element-77" data-mp="16ms6of4pnlyd">
                                  {loan.fileNumber} · {loan.state} ·{' '}
                                  {shortDate(loan.closedOn)}
                                </span>
                              </td>
                              <Money value={loan.loanAmount} strong data-id="element-78" data-mp="1kinla8s2uavy" />
                              <Money value={loan.ysplDollars} data-id="element-79" data-mp="1pc8gxu676srz" />
                              <Money value={loan.processingFeeCollected} note={math.feePlacement === 'processing' ? undefined : 'n/a in state'} data-id="element-80" data-mp="24udqcw6ko26c" />
                              <Money value={loan.discountPoints} data-id="element-81" data-mp="1m4e5ymlofb99" />
                              <Money value={loan.underwritingFeeCollected} note={math.feePlacement === 'underwriting' ? undefined : 'n/a in state'} data-id="element-82" data-mp="1m32zgf0ujpeg" />
                              <Money value={math.net} strong data-id="element-83" data-mp="136uuzdfnrik7" />
                              <td className="px-2 py-3" data-id="element-84" data-mp="18562hhihdgz9">
                                <PointsBadge actualBps={math.actualBps} isShort={math.isPointShort} data-id="element-85" data-mp="186pid7085q0i" />
                                {math.isFeeShort && <span className="mt-1 flex items-center gap-1 text-[11px] font-semibold text-short" data-id="element-86" data-mp="yi23huj1rk7p">
                                    <AlertTriangleIcon className="h-3 w-3" data-id="element-87" data-mp="tgcqi9c9kwgz" />
                                    {math.feePlacementLabel} short
                                  </span>}
                                {loan.toleranceCure > 0 && <span className="mt-0.5 block font-mono text-[11px] text-short" data-id="element-88" data-mp="1h6gskc9t0o5v">
                                    −{usd(loan.toleranceCure)} cure
                                  </span>}
                              </td>
                              <td className="px-2 py-3" data-id="element-89" data-mp="1m6v07t7htw3d">
                                <div className="flex items-center gap-1.5" data-id="element-90" data-mp="1m8fx3pha5r4e">
                                  <label className="sr-only" htmlFor={`bps-${loan.id}-${row.staff.id}`} data-id="element-91" data-mp="1tb49wqzw0gs6">
                                    {row.staff.name} basis point rate on file{' '}
                                    {loan.fileNumber}
                                  </label>
                                  <input id={`bps-${loan.id}-${row.staff.id}`} type="number" min={0} max={FULL_COMP_BPS} step={5} value={file.entitledBps} onChange={event => {
                            const next = Number(event.target.value);
                            onOverride(loan.id, row.staff.id, Number.isFinite(next) ? next : undefined);
                          }} className={`w-20 rounded-sm border px-2 py-1 text-right font-mono text-sm tabular-nums transition-colors duration-150 ease-out focus:outline-none focus-visible:ring-2 focus-visible:ring-orange ${file.isOverridden ? 'border-orange bg-orange-soft text-orange-dark' : 'border-line-strong bg-surface text-ink'}`} data-id="element-92" data-mp="2bz9cvw8pzxpx" />
                                  <span className="text-[11px] text-ink-3" data-id="element-93" data-mp="28ctb1tttai1g">bps</span>
                                  {file.isOverridden && <button type="button" onClick={() => onOverride(loan.id, row.staff.id, undefined)} aria-label={`Reset to standard ${file.baselineBps} bps`} className="rounded-sm border border-line p-1 text-ink-3 transition-colors duration-150 ease-out hover:bg-surface hover:text-ink focus:outline-none focus-visible:ring-2 focus-visible:ring-orange" data-id="element-94" data-mp="23cyk5qa1dsge">
                                      <RotateCcwIcon className="h-3.5 w-3.5" data-id="element-95" data-mp="is9xfsj301p7" />
                                    </button>}
                                </div>
                                <span className="mt-1 block font-mono text-[11px] text-ink-3" data-id="element-96" data-mp="9vr80qa26vxd">
                                  {percent(file.shareOfFullComp)} of {FULL_COMP_BPS} bps
                                </span>
                              </td>
                              <td className="py-3 pl-2 text-right font-mono text-sm font-semibold tabular-nums text-ink" data-id="element-97" data-mp="2mzl18x4f7fcg">
                                {usd(file.payoutDollars)}
                              </td>
                            </tr>;
                  })}
                      </tbody>
                    </table>
                  </div>
                </div>}
            </li>;
      })}
      </ul>
    </section>;
}
function Money({
  value,
  strong = false,
  note
}: {
  value: number;
  strong?: boolean;
  note?: string;
}) {
  return <td className="px-2 py-3 text-right" data-id="element-98" data-mp="2d38b29833jmg">
      <span className={`block font-mono text-sm tabular-nums ${value === 0 ? 'text-ink-3' : strong ? 'font-semibold text-ink' : 'text-ink-2'}`} data-id="element-99" data-mp="2d4az89e0v7yo">
        {usd(value)}
      </span>
      {note && <span className="block text-[10px] text-ink-3" data-id="element-100" data-mp="1fnm2uv7ucxwo">{note}</span>}
    </td>;
}
function initials(name: string): string {
  const parts = name.trim().split(' ');
  return `${parts[0]?.[0] ?? ''}${parts[1]?.[0] ?? ''}`.toUpperCase();
}